import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, ShoppingCart, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const { t, i18n } = useTranslation();
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useApp();
  const [selectedColor, setSelectedColor] = useState(product.colors?.[0] || null);
  const [isHovered, setIsHovered] = useState(false);

  const inWishlist = isInWishlist(product.id);
  const lang = i18n.language;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const success = await addToCart(product.id, 1, selectedColor);
    if (success) {
      toast.success(t('shop.addToCart') + ' ✓', {
        description: product.name[lang] || product.name.en,
        style: { background: '#1A1A1A', border: '1px solid #FF0099', color: '#fff' }
      });
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inWishlist) {
      await removeFromWishlist(product.id);
      toast.success(t('shop.removeFromWishlist') + ' ✓', {
        style: { background: '#1A1A1A', border: '1px solid #333', color: '#fff' }
      });
    } else {
      await addToWishlist(product.id);
      toast.success(t('shop.addToWishlist') + ' ❤️', {
        style: { background: '#1A1A1A', border: '1px solid #FF0099', color: '#fff' }
      });
    }
  };

  return (
    <div 
      className="card-product relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`product-card-${product.id}`}
    >
      {/* Badge */}
      {product.badge && (
        <div className={`absolute top-3 left-3 z-10 ${product.badge === 'NEW' ? 'badge-new' : 'badge-sale'}`}>
          {product.badge}
        </div>
      )}

      {/* Wishlist Button */}
      <button
        onClick={handleWishlistToggle}
        className="absolute top-3 right-3 z-10 p-2 bg-black/50 backdrop-blur-sm rounded-full transition-all hover:bg-[#FF0099]"
        data-testid={`wishlist-btn-${product.id}`}
      >
        <Heart 
          className={`w-4 h-4 ${inWishlist ? 'fill-[#FF0099] text-[#FF0099]' : 'text-white'}`} 
        />
      </button>

      {/* Image */}
      <Link to={`/product/${product.id}`}>
        <div className="relative aspect-square overflow-hidden bg-[#0a0a0a]">
          <img
            src={product.image_url}
            alt={product.name[lang] || product.name.en}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          
          {/* Overlay on hover */}
          <div className={`absolute inset-0 bg-black/60 flex items-center justify-center gap-3 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <Link 
              to={`/product/${product.id}`}
              className="p-3 bg-white text-black rounded-full hover:bg-[#FF0099] hover:text-white transition-colors"
              data-testid={`view-product-${product.id}`}
            >
              <Eye className="w-5 h-5" />
            </Link>
            <button
              onClick={handleAddToCart}
              disabled={!product.in_stock}
              className="p-3 bg-[#FF0099] text-white rounded-full hover:bg-[#FAFF00] hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`add-cart-btn-${product.id}`}
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
          </div>
        </div>
      </Link>

      {/* Info */}
      <div className="p-4">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-heading text-white text-sm font-bold uppercase tracking-wider mb-1 hover:text-[#FF0099] transition-colors">
            {product.name[lang] || product.name.en}
          </h3>
        </Link>
        
        <p className="text-[#FF0099] font-bold text-xl">
          €{product.price.toFixed(2)}
        </p>

        {/* Color Options */}
        {product.colors && product.colors.length > 0 && (
          <div className="flex gap-1 mt-3 flex-wrap">
            {product.colors.slice(0, 5).map((color) => (
              <button
                key={color}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedColor(color);
                }}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  selectedColor === color 
                    ? 'border-[#FF0099] scale-110' 
                    : 'border-transparent hover:border-white/50'
                }`}
                style={{ 
                  background: getColorValue(color)
                }}
                title={color}
                data-testid={`color-${color}`}
              />
            ))}
            {product.colors.length > 5 && (
              <span className="text-xs text-[#A3A3A3] self-center ml-1">
                +{product.colors.length - 5}
              </span>
            )}
          </div>
        )}

        {/* Stock Status */}
        {!product.in_stock && (
          <p className="text-[#FF0000] text-xs font-bold uppercase mt-2">
            {t('shop.outOfStock')}
          </p>
        )}
      </div>
    </div>
  );
};

// Helper to map color names to CSS values
const getColorValue = (colorName) => {
  const colorMap = {
    'Rainbow': 'linear-gradient(135deg, #FF0099, #FAFF00, #00FFFF)',
    'Pink-Blue': 'linear-gradient(135deg, #FF69B4, #00BFFF)',
    'Yellow-Pink': 'linear-gradient(135deg, #FAFF00, #FF0099)',
    'Yellow': '#FAFF00',
    'Black': '#1A1A1A',
    'Red': '#FF0000',
    'Turquoise': '#00FFFF',
    'Leopard': '#8B4513',
    'Pink Gradient': 'linear-gradient(135deg, #FFB6C1, #FF69B4)',
    'Pink': '#FF69B4',
    'White': '#FFFFFF',
    'Purple': '#9B30FF',
    'Mixed Colors': 'linear-gradient(135deg, #FF0099, #FAFF00, #00FFFF, #9B30FF)'
  };
  return colorMap[colorName] || '#666666';
};
